import { CircleHelp, HandCoins, Wallet } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"

import { Aparece } from "@/componentes/ui/Aparece"
import { Pestanas } from "@/componentes/ui/Pestanas"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

import { CuentasPorCobrar } from "./CuentasPorCobrar"
import { CuentasPorPagar } from "./CuentasPorPagar"

export const VISTAS = [
  { valor: "por-pagar", etiqueta: "Cuentas por pagar", icono: Wallet, permiso: "cxp:ver" },
  { valor: "por-cobrar", etiqueta: "Cuentas por cobrar", icono: HandCoins, permiso: "cxc:ver" },
] as const

type Vista = (typeof VISTAS)[number]["valor"]

/** Direcciones de cuando cada vista era su propia entrada del menú. */
const ANTIGUAS: Record<string, Vista> = { cxp: "por-pagar", cxc: "por-cobrar" }

const SUBTITULO: Record<Vista, string> = {
  "por-pagar": "Lo que la agencia le debe a sus proveedores, con sus abonos y su respaldo.",
  "por-cobrar": "Lo que los clientes le deben a la agencia, con lo que ya se les ha aplicado.",
}

/** El «?»: las reglas de cada vista que la pantalla no dice sola. */
const AYUDA: Record<Vista, readonly string[]> = {
  "por-pagar": [
    "Un abono parcial deja la cuenta PARCIAL, no PAGADO. El estado se deriva de los abonos vivos, nunca se teclea.",
    "El soporte es el documento que MOTIVA la deuda —la factura del proveedor, su nota de entrega—, no el comprobante del pago: ése va en el movimiento del banco o en la ejecución de la orden.",
    "El abono que se registra aquí no ata ningún movimiento del banco. Los que nacieron atados a su movimiento se crearon desde el registro de la conciliación.",
    "Anular un abono reabre la cuenta y no toca el banco; anular la cuenta entera sí revierte sus movimientos.",
  ],
  "por-cobrar": [
    "Una aplicación parcial deja la cuenta PARCIAL. El estado sale del ledger de aplicaciones, no de quien cobra.",
    "El importador no ve nunca estas pantallas: aquí está el costo del proveedor, que es el margen de la agencia.",
  ],
}

function vistaDe(enRuta: string | undefined): Vista {
  if (enRuta === undefined) return "por-pagar"
  return VISTAS.find((v) => v.valor === enRuta)?.valor ?? ANTIGUAS[enRuta] ?? "por-pagar"
}

/**
 * Libro de Cuentas: UNA entrada del menú con por pagar y por cobrar en pestañas.
 *
 * ⚠ **Eran dos entradas, y las dos nacieron DESACTIVADAS.** Las pantallas existían
 * desde el módulo de liquidación y no había forma de llegar a ellas sin teclear la URL.
 * Se juntan por el mismo motivo que Tesorería juntó sus cuatro: la barra lateral no
 * crece con una línea por cada vista de lo mismo, y las dos caras de la misma deuda se
 * miran juntas.
 *
 * ⚠ **Cada pestaña pide SU permiso.** La entrada del menú cuelga de `cxp:ver` —hoy
 * `cxp:ver` y `cxc:ver` viajan siempre juntos—, pero quien sólo tenga uno de los dos ve
 * sólo su pestaña: esconder el enlace no es un permiso, y el servidor decide igual.
 *
 * ⚠ **La vista va en la URL** (`/libro-cuentas/por-pagar`), y las direcciones viejas
 * —`/cxp` y `/cxc`— llevan a su pestaña, como hizo Tesorería con `/tesoreria/cuentas`.
 */
export function LibroCuentas() {
  const { vista: enRuta } = useParams()
  const navegar = useNavigate()
  const usuario = usarSesion((e) => e.usuario)
  const visibles = VISTAS.filter((v) => tienePermiso(usuario, v.permiso))
  const pedida = vistaDe(enRuta)
  // Una pestaña que no se puede abrir cae a la primera que sí; si no hay ninguna, el
  // panel queda vacío y el servidor tampoco habría servido nada.
  const vista = visibles.some((v) => v.valor === pedida) ? pedida : (visibles[0]?.valor ?? pedida)
  const [ayuda, setAyuda] = useState(false)

  useEffect(() => {
    if (enRuta !== undefined && enRuta !== vista) {
      navegar(`/libro-cuentas/${vista}`, { replace: true })
    }
  }, [enRuta, vista, navegar])

  return (
    <div>
      <header className="mb-4">
        <div className="flex items-center gap-1">
          <h1 className="text-xl font-semibold">Libro de Cuentas</h1>
          <button
            type="button"
            aria-expanded={ayuda}
            aria-controls={ayuda ? "ayuda-libro-cuentas" : undefined}
            onClick={() => setAyuda((a) => !a)}
            className="foco-cristal toque-cristal inline-flex size-7 items-center justify-center rounded-[var(--radius-control)]"
            style={{ color: "var(--fg-muted)" }}
          >
            <CircleHelp size={16} aria-hidden={true} />
            <span className="sr-only">Cómo funciona esta pestaña</span>
          </button>
        </div>
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {SUBTITULO[vista]}
        </p>
        <Aparece visible={ayuda} className="pb-1">
          <div id="ayuda-libro-cuentas" className="tarjeta mt-3 max-w-2xl">
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {AYUDA[vista].map((linea) => (
                <li key={linea}>{linea}</li>
              ))}
            </ul>
          </div>
        </Aparece>
      </header>

      <Pestanas
        pestanas={visibles}
        activa={vista}
        onCambiar={(v) => navegar(`/libro-cuentas/${v}`, { replace: true })}
        etiqueta="Vistas del libro de cuentas"
        idPanel="panel-libro-cuentas"
        prefijoId="pestana-libro-cuentas"
      />

      <div
        id="panel-libro-cuentas"
        role="tabpanel"
        aria-labelledby={`pestana-libro-cuentas-${vista}`}
      >
        {vista === "por-pagar" && <CuentasPorPagar />}
        {vista === "por-cobrar" && <CuentasPorCobrar />}
      </div>
    </div>
  )
}
